import { useId, useState } from "react";

import { formatInteger } from "@/shared/utils/format";

import { EmptyState } from "./EmptyState";

/** Um ponto da série: `key` identifica (o período, um dia), `label` é
 *  o que aparece no eixo e na dica, `value` é o número já ESCOLHIDO
 *  pela tela — o gráfico não sabe se é menção, alcance ou dólar. */
export type LinePoint = { key: string; label: string; value: number };

/** Os dois desenhos da série. `classic` é o do dashboard (bolinha em
 *  cada ponto, o último sempre ativo); `minimal` (2026-10-22, pedido da
 *  dona pro consumo de IA: "só a linha, e a info do ponto quando passa o
 *  mouse") é a linha suave com a área esmaecida embaixo, sem ponto
 *  nenhum até o mouse ou o foco chegar — aí aparecem o ponto, uma guia
 *  vertical e a dica. */
export type LineChartVariant = "classic" | "minimal";

type Coordinate = { x: number; y: number };

/** Catmull-Rom → Bézier cúbica, com os pontos de controle presos à faixa
 *  do gráfico pra uma sequência de zeros não "afundar" abaixo da linha
 *  de base. */
function smoothPath(coordinates: Coordinate[], top: number, bottom: number): string {
  if (coordinates.length < 2) {
    return coordinates.map(point => `M${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(" ");
  }
  const clampY = (y: number) => Math.min(Math.max(y, top), bottom);
  const parts = [`M${coordinates[0].x.toFixed(1)},${coordinates[0].y.toFixed(1)}`];
  for (let index = 0; index < coordinates.length - 1; index += 1) {
    const previous = coordinates[index - 1] ?? coordinates[index];
    const current = coordinates[index];
    const next = coordinates[index + 1];
    const after = coordinates[index + 2] ?? next;
    const control1 = { x: current.x + (next.x - previous.x) / 6, y: clampY(current.y + (next.y - previous.y) / 6) };
    const control2 = { x: next.x - (after.x - current.x) / 6, y: clampY(next.y - (after.y - current.y) / 6) };
    parts.push(
      `C${control1.x.toFixed(1)},${control1.y.toFixed(1)} ${control2.x.toFixed(1)},${control2.y.toFixed(1)} ${next.x.toFixed(1)},${next.y.toFixed(1)}`
    );
  }
  return parts.join(" ");
}

/** Série temporal em SVG caseiro, com dica no ponto ativo (o último,
 *  ou o que estiver sob o mouse/foco). Ver o cabeçalho de `Panel.tsx`
 *  pela origem; `formatValue` é o que a tela usa pra escrever o número
 *  no eixo e na dica. */
export function LineChart({ points, formatValue = formatInteger, variant = "classic", height: requestedHeight }: {
  points: LinePoint[]; formatValue?: (value: number) => string; variant?: LineChartVariant; height?: number;
}) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const gradientId = useId();
  const minimal = variant === "minimal";
  const values = points.map(point => point.value);
  const max = Math.max(...values, 1);
  const width = 720;
  const height = requestedHeight ?? (minimal ? 150 : 230);
  const maxLabel = formatValue(max);
  // No desenho mínimo o rótulo do eixo é maior (pedido da dona: "a info no
  // eixo y maior"), então a margem esquerda cresce com o tamanho dele —
  // "US$ 17,49" não cabe nos 46 do clássico. ~11 unidades por caractere
  // na fonte de 18 unidades da folha de estilo.
  const paddingLeft = minimal ? Math.max(46, maxLabel.length * 11 + 14) : 46;
  const paddingRight = 14;
  const paddingTop = 16;
  const paddingBottom = 18;
  const plotWidth = width - paddingLeft - paddingRight;
  const plotHeight = height - paddingTop - paddingBottom;
  const coordinates = values.map((value, index) => ({
    x: paddingLeft + (index * plotWidth) / Math.max(values.length - 1, 1),
    y: height - paddingBottom - (value / max) * plotHeight
  }));
  const baseline = height - paddingBottom;
  const path = minimal
    ? smoothPath(coordinates, paddingTop, baseline)
    : coordinates.map((point, index) => `${index ? "L" : "M"}${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(" ");
  const areaPath = coordinates.length > 1
    ? `${path} L${coordinates[coordinates.length - 1].x.toFixed(1)},${baseline} L${coordinates[0].x.toFixed(1)},${baseline} Z`
    : "";
  // No desenho clássico o último ponto é o ativo por padrão; no mínimo
  // nada é ativo até o mouse ou o foco — a dica é só do ponto apontado.
  const activeIndex = hoveredIndex ?? (minimal ? null : coordinates.length - 1);
  const activePoint = activeIndex === null ? undefined : coordinates[activeIndex];
  // O balão é desenhado em unidades da caixa de 720 e encolhe com o SVG:
  // no desenho mínimo, num painel estreito, 116×44 virava 53×20px — ilegível
  // (print da dona, 2026-10-22). O dobro, com a letra também em unidades.
  const tooltipWidth = minimal ? 220 : 116;
  const tooltipHeight = minimal ? 84 : 44;
  const tooltipX = activePoint
    ? Math.min(Math.max(activePoint.x - tooltipWidth / 2, paddingLeft - 12), width - paddingRight - tooltipWidth + 12)
    : 0;
  const tooltipY = activePoint ? Math.max(activePoint.y - tooltipHeight - 12, 0) : 0;
  const gridSteps = minimal ? [.5, 1] : [.25, .5, .75, 1];
  // A largura da "coluna" invisível que cada ponto ocupa pro mouse.
  const column = coordinates.length > 1 ? plotWidth / (coordinates.length - 1) : plotWidth;

  if (!points.length) return <EmptyState/>;
  return (
    <div className={`dashboard-line-chart${minimal ? " is-minimal" : ""}`}>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`Evolução no período. Último valor: ${formatValue(values.at(-1) ?? 0)}`}>
        {minimal && (
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" className="dashboard-chart-gradient-start"/>
              <stop offset="1" className="dashboard-chart-gradient-end"/>
            </linearGradient>
          </defs>
        )}
        {gridSteps.map(value => <line key={value} x1={paddingLeft} x2={width - paddingRight} y1={height - paddingBottom - value * plotHeight} y2={height - paddingBottom - value * plotHeight} className="dashboard-chart-grid"/>)}
        <text x={paddingLeft - 8} y={paddingTop + 4} className="dashboard-chart-axis-label" textAnchor="end">{maxLabel}</text>
        <text x={paddingLeft - 8} y={baseline} className="dashboard-chart-axis-label" textAnchor="end">0</text>
        {areaPath && (minimal
          // Em `style`, não em atributo: o `fill` da folha de estilo venceria
          // o atributo e a área sairia sólida (conferido no navegador).
          ? <path d={areaPath} className="dashboard-chart-area is-minimal" style={{ fill: `url("#${gradientId}")` }}/>
          : <path d={areaPath} className="dashboard-chart-area"/>)}
        <path d={path} className="dashboard-chart-line"/>
        {minimal
          ? coordinates.map((point, index) => (
            // A coluna invisível é o alvo do mouse e do foco; o ponto só
            // aparece quando ela está ativa.
            <rect
              key={points[index].key}
              x={point.x - column / 2}
              y={paddingTop}
              width={column}
              height={plotHeight}
              tabIndex={0}
              // `aria-label`, não `<title>`: o `<title>` vira a dica nativa
              // do navegador, que aparecia ao lado do balão repetindo o
              // mesmo número (print da dona, 2026-10-22).
              aria-label={`${points[index].label}: ${formatValue(values[index])}`}
              className="dashboard-chart-hit"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              onFocus={() => setHoveredIndex(index)}
              onBlur={() => setHoveredIndex(null)}
            />
          ))
          : coordinates.map((point, index) => (
            <circle
              key={points[index].key}
              cx={point.x}
              cy={point.y}
              r={index === activeIndex ? 6 : 4}
              tabIndex={0}
              className={`dashboard-chart-point${index === activeIndex ? " is-active" : ""}`}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              onFocus={() => setHoveredIndex(index)}
              onBlur={() => setHoveredIndex(null)}
            >
              <title>{`${points[index].label}: ${formatValue(values[index])}`}</title>
            </circle>
          ))}
        {minimal && activePoint && (
          <>
            <line x1={activePoint.x} x2={activePoint.x} y1={paddingTop} y2={baseline} className="dashboard-chart-guide"/>
            <circle cx={activePoint.x} cy={activePoint.y} r={5} className="dashboard-chart-point is-active"/>
          </>
        )}
        {activePoint && activeIndex !== null && (
          <foreignObject x={tooltipX} y={tooltipY} width={tooltipWidth} height={tooltipHeight} className="dashboard-chart-tooltip-anchor">
            <div className="dashboard-chart-tooltip">
              <strong>{formatValue(values[activeIndex])}</strong>
              <span>{points[activeIndex].label}</span>
            </div>
          </foreignObject>
        )}
      </svg>
      {/* No desenho mínimo não há legenda de dias (pedido da dona): o dia
          aparece no balão do ponto apontado. */}
      {!minimal && <div className="dashboard-chart-axis" aria-hidden="true">
        {points.filter((_, index) => index === 0 || index === points.length - 1 || index % Math.max(1, Math.ceil(points.length / 5)) === 0).map(point => <span key={point.key}>{point.label}</span>)}
      </div>}
    </div>
  );
}
